import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { SpikeUserMenuComponent } from '../user-menu/spike-user-menu.component';
import { HlmIconComponent } from '../../../ui/icon.component';
import { HLM_MENU, HlmTooltipDirective } from '../../../ui';
import { FontScaleService } from '../../../core/font-scale/font-scale.service';
import { ThemeService } from '../../../core/theme/theme.service';
import { GlobalSearchService } from '../../../shared/global-search/global-search.service';

@Component({
  selector: 'spike-titlebar',
  standalone: true,
  imports: [SpikeUserMenuComponent, HlmIconComponent, HlmTooltipDirective, ...HLM_MENU],
  // The host must be a layout-pass-through so the inner <header sticky> can
  // anchor to the flex parent (cmt-root > div) — otherwise sticky would only
  // stick inside the host's tiny static box and scroll away with it.
  host: { class: 'contents' },
  template: `
    <header class="sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur-xl">
      <div class="relative flex h-14 items-center px-8">
        <!-- AMIE wordmark -->
        <button
          type="button"
          (click)="goHome()"
          class="group flex items-center gap-2.5 outline-none"
          title="AMIE — All-in-one Management & Integration Engine"
        >
          <img
            src="AMIE_logo_transparent.png"
            alt="AMIE"
            class="h-12 w-auto shrink-0 transition-transform group-hover:scale-105"
          />
        </button>

        <!-- Single global search entry — opens the unified search + quicklinks overlay
             (EB-3621 Plan 5.3). Same overlay as Ctrl/Cmd+K. Hidden on the landing page,
             which has its own hero search (avoid a redundant second search entry).
             Absolutely centered in the header (independent of the logo/controls widths on
             either side) so it reads as the app's primary entry point. -->
        @if (!isLanding()) {
          <div class="pointer-events-none absolute inset-0 flex items-center justify-center px-8">
            <button
              type="button"
              (click)="openGlobalSearch()"
              class="group pointer-events-auto flex h-9 w-full max-w-md items-center gap-2.5 rounded-full border border-border bg-card px-4 text-sm text-muted-foreground transition-all hover:border-foreground/30 hover:text-foreground"
              aria-label="Open search"
            >
              <hlm-icon name="lucideSearch" size="0.95rem" />
              <span class="flex-1 truncate text-left">Search customers, actions…</span>
              <span class="font-mono-feat rounded border border-border bg-muted px-1.5 py-0.5 text-sm text-muted-foreground">
                ⌃K
              </span>
            </button>
          </div>
        }

        <div class="ml-auto flex items-center gap-3">
          <spike-user-menu />

          <!-- Appearance menu (theme / style / font size) -->
          <hlm-menu align="end" width="min-w-[280px]">
            <button
              hlmMenuTrigger
              type="button"
              hlmTooltip="Appearance"
              position="bottom"
              class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-all hover:border-foreground/30 hover:text-foreground"
              aria-label="Appearance"
            >
              <hlm-icon name="lucideSlidersHorizontal" size="1rem" />
            </button>
            <ng-template hlmMenuContent>
              <!-- Appearance section -->
              <div class="font-mono-feat px-3 pb-1 pt-2 text-base uppercase tracking-[0.18em] text-muted-foreground">
                Appearance
              </div>

              <!-- Theme mode (Light / Dark) — click to toggle -->
              <button hlmMenuItem (click)="themeService.toggleMode(); $event.stopPropagation()">
                <hlm-icon [name]="themeService.isDark() ? 'lucideSun' : 'lucideMoon'" size="1rem" class="text-muted-foreground" />
                <span class="flex-1 text-left">Theme</span>
                <span class="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-0.5 font-mono-feat text-base text-foreground">
                  {{ themeService.isDark() ? 'Dark' : 'Light' }}
                  <hlm-icon name="lucideRefreshCw" size="0.75rem" class="text-muted-foreground" />
                </span>
              </button>

              <!-- Style cycle — click to cycle -->
              <button hlmMenuItem (click)="themeService.toggleStyle(); $event.stopPropagation()">
                <hlm-icon name="lucideSparkles" size="1rem" class="text-accent" />
                <span class="flex-1 text-left">Style</span>
                <span class="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-0.5 font-mono-feat text-base text-foreground">
                  {{ themeService.style().label }}
                  <hlm-icon name="lucideRefreshCw" size="0.75rem" class="text-muted-foreground" />
                </span>
              </button>

              <!-- Font scale row -->
              <div class="flex items-center gap-2 px-3 py-2">
                <hlm-icon name="lucideType" size="1rem" class="text-muted-foreground" />
                <span class="flex-1 text-base text-foreground">Font size</span>
                <div class="inline-flex items-center gap-0.5 rounded-full border border-border bg-muted/40 px-1 py-0.5">
                  <button
                    type="button"
                    (click)="fontScale.decrease(); $event.stopPropagation()"
                    [disabled]="!fontScale.canDecrease()"
                    class="inline-flex h-7 w-7 items-center justify-center rounded-full text-base font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
                    aria-label="Decrease font size"
                  >A-</button>
                  <button
                    type="button"
                    (click)="fontScale.reset(); $event.stopPropagation()"
                    class="inline-flex h-7 min-w-[3rem] items-center justify-center rounded-full font-mono-feat text-base text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label="Reset font size"
                  >{{ fontScale.scalePercent() }}%</button>
                  <button
                    type="button"
                    (click)="fontScale.increase(); $event.stopPropagation()"
                    [disabled]="!fontScale.canIncrease()"
                    class="inline-flex h-7 w-7 items-center justify-center rounded-full text-base font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
                    aria-label="Increase font size"
                  >A+</button>
                </div>
              </div>
            </ng-template>
          </hlm-menu>
        </div>
      </div>
    </header>
  `,
})
export class SpikeTitlebarComponent {
  private router = inject(Router);
  private globalSearchService = inject(GlobalSearchService);
  readonly fontScale = inject(FontScaleService);
  readonly themeService = inject(ThemeService);

  /** The landing page has its own hero search, so the header search entry is hidden there. */
  private readonly currentUrl = toSignal(
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd), map(() => this.router.url)),
    { initialValue: this.router.url },
  );
  protected readonly isLanding = computed(() => this.currentUrl() === '/');

  goHome(): void {
    this.router.navigate(['/']);
  }

  openGlobalSearch(): void {
    this.globalSearchService.open();
  }
}
